import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDatabase, ref, get } from "firebase/database";
import { FaArrowLeft, FaUser, FaGraduationCap, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaTimes } from 'react-icons/fa';
import img1 from '../../assets/img1.jpeg';

function PublicUserDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const db = getDatabase();
                const userRef = ref(db, `Matrimony/users/${id}`);
                const snapshot = await get(userRef);
                if (snapshot.exists()) {
                    setUser(snapshot.val());
                } else {
                    console.log("User not found");
                }
            } catch (error) {
                console.error("Error fetching user:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
    }, [id]);

    const calculateAge = (dob) => {
        if (!dob) return "Not specified";
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const isImageUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        return url.startsWith('http://') || url.startsWith('https://');
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p style={styles.loadingText}>Loading public profile...</p>
            </div>
        );
    }

    if (!user) {
        return (
            <div style={styles.errorContainer}>
                <h2>User profile not found</h2>
                <button
                    onClick={() => navigate('/')}
                    style={styles.backButton}
                >
                    <FaArrowLeft /> Back to Home
                </button>
            </div>
        );
    }

    const personal = user.personal || {};
    const educational = user.educational || {};
    const contact = user.contact || {};
    const fullName = [personal.firstName, personal.middleName, personal.lastName].filter(Boolean).join(" ");
    const validPhotos = user.photos ? user.photos.filter(photo => photo && isImageUrl(photo)) : [];

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setSelectedImage(null);
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <header style={styles.siteHeader}>
                <div style={styles.headerContent}>
                    <div style={styles.logoSection} onClick={() => navigate('/')}>
                        <img src={img1} alt="Logo" style={styles.logo} />
                        <h1 style={styles.siteTitle}>Swrajya Sangam</h1>
                    </div>
                </div>
            </header>

            <main style={styles.mainContent}>
                <button
                    onClick={() => navigate(-1)}
                    style={styles.backButton}
                >
                    <FaArrowLeft /> Back
                </button>

                {/* Profile Header Card */}
                <div style={styles.profileHeader}>
                    <div style={styles.profileImageContainer}>
                        {validPhotos.length > 0 ? (
                            <img
                                src={validPhotos[0]}
                                alt="User avatar"
                                style={styles.profileImage}
                                onClick={() => handleImageClick(validPhotos[0])}
                            />
                        ) : (
                            <div style={styles.placeholderImage}>
                                <FaUser style={{ fontSize: '4rem', color: '#64748b' }} />
                            </div>
                        )}
                    </div>
                    <div style={styles.profileInfo}>
                        <h1 style={styles.name}>{fullName || "Name not provided"}</h1>
                        <div style={styles.basicInfo}>
                            <div style={styles.infoItem}>
                                <FaCalendarAlt style={styles.icon} />
                                <span>{calculateAge(personal.dateOfBirth)} years old</span>
                            </div>
                            <div style={styles.infoItem}>
                                <FaMapMarkerAlt style={styles.icon} />
                                <span>{educational.currentPlace || educational.district || "Location not specified"}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <FaUser style={styles.icon} />
                                <span>{personal.gender || "Not specified"}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={styles.sectionsContainer}>
                    {/* Personal Information */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>
                            <FaUser style={styles.sectionIcon} />
                            Personal Information
                        </h2>
                        <div style={styles.detailsGrid}>
                            <div style={styles.detailItem}>
                                <strong>Full Name:</strong>
                                <span>{fullName || "Not provided"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Date of Birth:</strong>
                                <span>{personal.dateOfBirth || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Birth Time:</strong>
                                <span>{personal.birthTime || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Age:</strong>
                                <span>{calculateAge(personal.dateOfBirth)} years</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Height:</strong>
                                <span>
                                    {personal.heightFeet && personal.heightInches
                                        ? `${personal.heightFeet}' ${personal.heightInches}"`
                                        : "Not specified"}
                                </span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Gender:</strong>
                                <span>{personal.gender || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Marital Status:</strong>
                                <span>{personal.maritalStatus || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Religion:</strong>
                                <span>{personal.religion || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Caste:</strong>
                                <span>{personal.caste || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Inter-Caste Allowed:</strong>
                                <span>{personal.interCasteAllowed || "Not specified"}</span>
                            </div>
                        </div>
                    </div>

                    {/* Educational & Professional */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>
                            <FaGraduationCap style={styles.sectionIcon} />
                            Education & Career
                        </h2>
                        <div style={styles.detailsGrid}>
                            <div style={styles.detailItem}>
                                <strong>Education:</strong>
                                <span>{educational.education || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Profession:</strong>
                                <span>{educational.profession || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Current Place:</strong>
                                <span>{educational.currentPlace || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Native Place:</strong>
                                <span>{educational.nativePlace || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Taluka:</strong>
                                <span>{educational.taluka || "Not specified"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>District:</strong>
                                <span>{educational.district || "Not specified"}</span>
                            </div>
                        </div>
                    </div>

                    {/* Contact Details - RENDERED DIRECTLY */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>
                            <FaPhone style={styles.sectionIcon} />
                            Contact Information
                        </h2>
                        <div style={styles.detailsGrid}>
                            <div style={styles.detailItem}>
                                <strong>Phone Number:</strong>
                                <span>{personal.phoneNumber || "Not provided"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>WhatsApp Number:</strong>
                                <span>{contact.whatsappNumber || "Not provided"}</span>
                            </div>
                            <div style={styles.detailItem}>
                                <strong>Calling Number:</strong>
                                <span>{contact.callingNumber || "Not provided"}</span>
                            </div>
                        </div>
                    </div>

                    {/* Photo Gallery */}
                    {validPhotos.length > 0 && (
                        <div style={styles.section}>
                            <h2 style={styles.sectionTitle}>
                                📸 Photo Gallery
                            </h2>
                            <div style={styles.photoGallery}>
                                {validPhotos.map((photo, index) => (
                                    <div key={index} style={styles.photoContainer}>
                                        <img
                                            src={photo}
                                            alt={`User ${index + 1}`}
                                            style={styles.photo}
                                            onClick={() => handleImageClick(photo)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Biodata - RENDERED DIRECTLY */}
                    {user.biodata && isImageUrl(user.biodata) && (
                        <div style={styles.section}>
                            <h2 style={styles.sectionTitle}>
                                📄 Biodata Image
                            </h2>
                            <div style={styles.biodataContainer}>
                                <img
                                    src={user.biodata}
                                    alt="User document"
                                    style={styles.biodataImage}
                                    onClick={() => handleImageClick(user.biodata)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Image Modal */}
            {showImageModal && selectedImage && (
                <div style={styles.imageModalOverlay} onClick={closeImageModal}>
                    <div style={styles.imageModalContainer} onClick={(e) => e.stopPropagation()}>
                        <button onClick={closeImageModal} style={styles.imageModalCloseButton}>
                            <FaTimes style={{ fontSize: '1.5rem' }} />
                        </button>
                        <img src={selectedImage} alt="Enlarged" style={styles.imageModalImage} />
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        fontFamily: 'Arial, sans-serif',
        background: '#f8fafc',
        minHeight: '100vh',
    },
    siteHeader: {
        background: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        padding: '1rem 2rem',
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: 1000,
        boxSizing: 'border-box'
    },
    headerContent: {
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center'
    },
    logoSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.8rem',
        cursor: 'pointer'
    },
    logo: {
        width: '40px',
        height: '40px',
        borderRadius: '8px'
    },
    siteTitle: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        margin: 0,
        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
    },
    mainContent: {
        padding: '100px 2rem 40px',
        maxWidth: '1200px',
        margin: '0 auto'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '5px solid #f3f3f3',
        borderTop: '5px solid #6a11cb',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '1rem'
    },
    loadingText: {
        fontSize: '1.2rem',
        color: '#64748b'
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        textAlign: 'center'
    },
    backButton: {
        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
        color: 'white',
        border: 'none',
        padding: '0.7rem 1.4rem',
        borderRadius: '25px',
        cursor: 'pointer',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '2rem'
    },
    profileHeader: {
        background: 'white',
        padding: '2rem',
        borderRadius: '20px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '2rem',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: '2rem'
    },
    profileImageContainer: {
        position: 'relative'
    },
    profileImage: {
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '4px solid #6a11cb',
        cursor: 'pointer'
    },
    placeholderImage: {
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: '#e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '4px solid #6a11cb'
    },
    profileInfo: {
        flex: 1
    },
    name: {
        fontSize: '2.2rem',
        fontWeight: 'bold',
        margin: '0 0 0.8rem 0',
        color: '#1e293b'
    },
    basicInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem'
    },
    infoItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        color: '#64748b'
    },
    icon: {
        color: '#6a11cb'
    },
    sectionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem'
    },
    section: {
        background: 'white',
        padding: '2rem',
        borderRadius: '20px',
        boxShadow: '0 5px 15px rgba(0,0,0,0.05)'
    },
    sectionTitle: {
        fontSize: '1.6rem',
        fontWeight: 'bold',
        color: '#1e293b',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.7rem',
        borderBottom: '1px solid #f1f5f9',
        paddingBottom: '0.8rem'
    },
    sectionIcon: {
        color: '#6a11cb'
    },
    detailsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.2rem'
    },
    detailItem: {
        padding: '1rem',
        background: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #f1f5f9'
    },
    photoGallery: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '1rem'
    },
    photoContainer: {
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
    },
    photo: {
        width: '100%',
        height: '300px',
        objectFit: 'cover',
        cursor: 'pointer'
    },
    biodataContainer: {
        display: 'flex',
        justifyContent: 'center'
    },
    biodataImage: {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        cursor: 'pointer'
    },
    imageModalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '2rem'
    },
    imageModalContainer: {
        position: 'relative',
        maxWidth: '90vw',
        maxHeight: '90vh'
    },
    imageModalCloseButton: {
        position: 'absolute',
        top: '-40px',
        right: 0,
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer'
    },
    imageModalImage: {
        maxWidth: '100%',
        maxHeight: '90vh',
        borderRadius: '8px'
    }
};

export default PublicUserDetail;
